import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getWaveformPeaks, readCachedPeaks, type WaveformPeaks } from "../../lib/waveform";

/**
 * Reine Canvas-Visualisierung einer Audio-Spur. Hört auf Resize und zeichnet
 * neu, wenn `windowStartSec` / `windowDurSec` sich ändern (z. B. weil der
 * User das Voiceover nach links/rechts trimmt). Das Schöne dabei: Wir
 * decodieren das Quell-File nur einmal, anschliessend ist jeder Repaint
 * sub-millisekunden-billig (nur ein paar tausend Linien-Strokes).
 *
 * Props:
 *  - `src`            : Audio-URL (gleicher String → gemeinsamer Cache)
 *  - `windowStartSec` : ab welcher Sekunde der Quelldatei wird angezeigt
 *  - `windowDurSec`   : wie viele Sekunden des Files werden über die Bar
 *                       gestreckt (entspricht der sichtbaren Bar-Dauer)
 *  - `colorTop` /
 *    `colorBottom`    : Farb-Gradient von oben nach unten (RGB-Strings)
 *  - `mirror`         : ob die untere Hälfte gespiegelt mitgezeichnet wird
 */
export const WaveformCanvas: React.FC<{
  src: string;
  windowStartSec?: number;
  windowDurSec?: number;
  colorTop?: string;
  colorBottom?: string;
  background?: string;
  className?: string;
  mirror?: boolean;
}> = ({
  src,
  windowStartSec = 0,
  windowDurSec,
  colorTop = "rgba(94, 234, 212, 0.95)",
  colorBottom = "rgba(13, 148, 136, 0.85)",
  background = "transparent",
  className,
  mirror = true,
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [peaks, setPeaks] = useState<WaveformPeaks | null>(() => readCachedPeaks(src));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setPeaks(readCachedPeaks(src));
    if (!src) return;
    getWaveformPeaks(src)
      .then((res) => {
        if (cancelled) return;
        setPeaks(res);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [src]);

  // Repaint when peaks/window/size change.
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const dpr = window.devicePixelRatio || 1;
    const cssW = container.clientWidth;
    const cssH = container.clientHeight;
    if (cssW <= 0 || cssH <= 0) return;
    canvas.width = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);
    if (background !== "transparent") {
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, cssW, cssH);
    }

    if (!peaks) return;

    const totalDur = peaks.durationSec || 0;
    if (totalDur <= 0) return;

    // Map des sichtbaren Fensters → Bucket-Bereich
    const winStart = Math.max(0, windowStartSec);
    const winDur = Math.max(0.01, windowDurSec ?? totalDur - winStart);
    const winEnd = winStart + winDur;
    const startFrac = Math.min(1, Math.max(0, winStart / totalDur));
    const endFrac = Math.min(1, Math.max(startFrac, winEnd / totalDur));
    const startBucket = Math.floor(startFrac * peaks.peaks.length);
    const endBucket = Math.max(startBucket + 1, Math.ceil(endFrac * peaks.peaks.length));
    const visibleBuckets = endBucket - startBucket;

    const mid = mirror ? cssH / 2 : cssH;
    const grad = ctx.createLinearGradient(0, 0, 0, cssH);
    grad.addColorStop(0, colorTop);
    grad.addColorStop(1, colorBottom);
    ctx.fillStyle = grad;

    // Pixel-genaues Sampling: für jede X-Pixel-Spalte den max-Bucket im
    // entsprechenden Quell-Range. Das vermeidet Aliasing bei Zoom.
    const cols = Math.max(1, Math.floor(cssW));
    for (let x = 0; x < cols; x += 1) {
      const fromBucket = Math.floor(startBucket + (x / cols) * visibleBuckets);
      const toBucket = Math.max(
        fromBucket + 1,
        Math.floor(startBucket + ((x + 1) / cols) * visibleBuckets),
      );
      let peak = 0;
      for (let b = fromBucket; b < toBucket && b < peaks.peaks.length; b += 1) {
        if (peaks.peaks[b] > peak) peak = peaks.peaks[b];
      }
      // Schmaler 1px-Strich mit min-Höhe damit Stille-Bereiche sichtbar bleiben.
      const h = Math.max(1, peak * (mirror ? cssH * 0.48 : cssH * 0.95));
      if (mirror) {
        ctx.fillRect(x, mid - h, 1, h * 2);
      } else {
        ctx.fillRect(x, cssH - h, 1, h);
      }
    }
  }, [peaks, windowStartSec, windowDurSec, colorTop, colorBottom, background, mirror]);

  // Resize observer — repaint when bar resizes (zoom, drag).
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !("ResizeObserver" in window)) return;
    const ro = new ResizeObserver(() => {
      // Trigger by toggling a state? Instead force-paint via the layout effect
      // dependency. Simplest: read current peaks and reset state to same val.
      setPeaks((p) => (p ? { ...p } : p));
    });
    ro.observe(container);
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={containerRef} className={`waveform-canvas-wrap ${className ?? ""}`}>
      <canvas ref={canvasRef} aria-hidden />
      {error && !peaks ? <span className="waveform-error">⚠</span> : null}
    </div>
  );
};
